import { useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Divider } from "@mui/material";
import { FaTimes, FaPlus } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import GuestBadges from "components/GuestBadges";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";
import dayjs from "dayjs";
import { RiSparkling2Line } from "react-icons/ri";

const glassCardSX = {
  background: "linear-gradient(135deg, rgba(32,42,52,0.72), rgba(32,42,52,0.48))",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(255,255,255,0.14)",
};

const formatDate = (date) => {
  if (!date) return "—";
  return dayjs(date).format("MMM D, YYYY");
};
const formatShortDate = (date) => {
  if (!date) return "—";
  return dayjs(date).format("MMM D, YYYY");
};

const getStayDuration = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return "—";

  const nights = dayjs(checkOut).diff(dayjs(checkIn), "day");
  if (nights <= 0) return "—";

  return `${nights} night${nights > 1 ? "s" : ""}`;
};

const Info = ({ label, value }) => (
  <VuiBox display="flex" flexDirection="column">
    <VuiTypography
      variant="caption"
      sx={{
        mb: 0.6,
        lineHeight: 1,
      }}
    >
      {label}
    </VuiTypography>

    <VuiTypography
      variant="body2"
      sx={{
        lineHeight: 1.35,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </VuiTypography>
  </VuiBox>
);

const NotesSection = ({ title, notes }) => {
  return (
    <VuiBox mb={1.3} p={1.5} borderRadius="16px" sx={glassCardSX}>
      <VuiTypography variant="h6" mb={1}>
        {title}
      </VuiTypography>

      {notes.length ? (
        notes.map((note, index) => (
          <VuiBox
            key={index}
            p={1.5}
            mb={1}
            borderRadius="12px"
            sx={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <VuiTypography variant="body2" mb={0} sx={{ fontSize: "14px" }}>
              {note.text}
            </VuiTypography>

            <VuiTypography variant="caption">
              {note.staff ? `${note.staff} • ` : ""}
              {formatDate(note.date)}
            </VuiTypography>
          </VuiBox>
        ))
      ) : (
        <VuiTypography variant="caption">No {title.toLowerCase()} available</VuiTypography>
      )}
    </VuiBox>
  );
};
const GuestTypeBadge = ({ type }) => {
  const config = {
    VIP: {
      bg: "rgba(156, 39, 176, 0.18)",
      border: "rgba(156, 39, 176, 0.45)",
      color: "#d05ce3",
    },
    Frequent: {
      bg: "rgba(0, 76, 127, 0.18)",
      border: "rgba(0, 76, 127, 0.45)",
      color: "#4fc3f7",
    },
    Returning: {
      bg: "rgba(0, 150, 136, 0.18)",
      border: "rgba(0, 150, 136, 0.45)",
      color: "#4db6ac",
    },
    New: {
      bg: "rgba(255,255,255,0.08)",
      border: "rgba(255,255,255,0.18)",
      color: "#b0bec5",
    },
  };

  const style = config[type] || config.New;

  return (
    <VuiBox
      px={1.4}
      py={0.4}
      borderRadius="10px"
      sx={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontSize: "11px",
        fontWeight: 500,
        textTransform: "uppercase",
      }}
    >
      {type}
    </VuiBox>
  );
};

const GuestDetailsDialog = ({ open, onClose, guest }) => {
  if (!guest) return null;

  const arrivalNotes = guest.notes?.filter((n) => n.type === "arrival") || [];
  const staffNotes = guest.notes?.filter((n) => n.type === "staff") || [];
  const pmsNotes = guest.notes?.filter((n) => n.type === "pms") || [];

  const summaryError = false;
  const summaryGenerating = false;
  const aiSummary = "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <VuiTypography variant="h6">Profile & History</VuiTypography>

        <IconButton onClick={onClose}>
          <FaTimes size={14} color="#fff" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* ===== GUEST OVERVIEW ===== */}
        <VuiBox mb={2} p={2} borderRadius="16px" sx={glassCardSX}>
          <VuiBox display="flex" justifyContent="space-between" mb={2}>
            <VuiTypography variant="h6">Guest Overview</VuiTypography>
          </VuiBox>

          <VuiBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
            <VuiBox>
              <Info label="Guest Name" value={guest.name} />
              <GuestBadges badges={guest.badges} />
            </VuiBox>
            <Info label="Email" value={guest.email} />
            <Info label="Phone Number" value={guest.phone} />
            <Info label="Nationality" value={guest.nationality} />
            <Info label="Language" value={guest.language} />
            <Info label="Total Stays" value={guest.stays} />
          </VuiBox>

          <VuiBox mt={1.5} pt={0} borderTop="1px solid rgba(255,255,255,0.12)">
            <VuiTypography variant="caption" sx={{ letterSpacing: "0.04em" }}>
              Guest Profile Summary (AI)
            </VuiTypography>

            {summaryError && (
              <VuiBox
                mt={1}
                py={0.5}
                px={1.5}
                borderRadius="md"
                sx={{
                  background: "rgba(255, 0, 0, 0.08)",
                  border: "1px solid rgba(255, 0, 0, 0.25)",
                }}
              >
                <VuiTypography variant="caption" color="error">
                  {/* {summaryError?.message || "AI summary generation failed. Please try again."} */}
                </VuiTypography>
              </VuiBox>
            )}

            {aiSummary ? (
              <VuiTypography
                variant="body2"
                mt={1}
                fontWeight="light"
                lineHeight={1.6}
                sx={{ fontSize: "14px", letterSpacing: "0.04em" }}
              >
                {aiSummary}
              </VuiTypography>
            ) : (
              <VuiBox display="flex" justifyContent="center" my={1}>
                <VuiButton
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RiSparkling2Line size={12} />}
                  // onClick={handleGenerateSummary}
                  // disabled={summaryGenerating}
                >
                  {summaryGenerating ? "Generating..." : "Generate"}
                </VuiButton>
              </VuiBox>
            )}

            {aiSummary && (
              <VuiTypography
                variant="caption"
                mt={1.5}
                sx={{
                  cursor: summaryGenerating ? "default" : "pointer",
                  opacity: summaryGenerating ? 0.6 : 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
                // onClick={!summaryGenerating ? handleGenerateSummary : undefined}
              >
                <RiSparkling2Line size={12} />
                {summaryGenerating ? "Generating..." : "Regenerate"}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>

        {/* ===== STAY HISTORY  ===== */}
        <VuiBox mb={2} p={2.5} borderRadius="16px" sx={glassCardSX}>
          <VuiTypography variant="h6" mb={2}>
            Stay History
          </VuiTypography>

          <VuiBox
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
            mb={2.5}
          >
            <Info label="First Stay" value={formatDate(guest.firstStay)} />
            <Info label="Last Stay" value={formatDate(guest.lastStay)} />
            <Info label="Total Stays" value={guest.stays} />
            <Info label="Avg Stay Duration" value="2.3 days" />
          </VuiBox>

          {guest.stayHistory?.length ? (
            <VuiBox>
              {guest.stayHistory.map((stay, index) => (
                <VuiBox
                  key={index}
                  py={1}
                  px={1.5}
                  mb={1.5}
                  borderRadius="12px"
                  sx={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <VuiBox
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1.5fr 1fr 1fr" }}
                    gap={2}
                    alignItems="center"
                  >
                    <VuiBox>
                      <VuiTypography variant="caption" mb={0.5}>
                        Stay Period
                      </VuiTypography>
                      <VuiTypography variant="body2" sx={{ fontSize: "14px" }}>
                        {formatShortDate(stay.checkIn)} → {formatShortDate(stay.checkOut)}
                      </VuiTypography>
                    </VuiBox>

                    <VuiBox>
                      <VuiTypography variant="caption" mb={0.5}>
                        Duration
                      </VuiTypography>
                      <VuiTypography variant="body2" sx={{ fontSize: "14px" }}>
                        {getStayDuration(stay.checkIn, stay.checkOut)}
                      </VuiTypography>
                    </VuiBox>

                    <VuiBox>
                      <VuiTypography variant="caption" mb={0.5}>
                        Room
                      </VuiTypography>
                      <VuiTypography variant="body2" sx={{ fontSize: "14px" }}>
                        {stay.room || "—"}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>
              ))}
            </VuiBox>
          ) : (
            <VuiTypography variant="caption">No stay history available</VuiTypography>
          )}
        </VuiBox>

        {/* ===== PREFERENCES ===== */}
        <VuiBox mb={2} p={2} borderRadius="16px" sx={glassCardSX}>
          <VuiTypography variant="h6" mb={2}>
            Preferences Completion
          </VuiTypography>

          <VuiProgress value={guest.preferences?.percentage || 0} />

          <VuiTypography variant="caption">
            {guest.preferences?.completed || 0}/{guest.preferences?.total || 0} completed
          </VuiTypography>
        </VuiBox>

        {/* ===== NOTES ===== */}
        <VuiBox mb={2} p={2.5} borderRadius="16px" sx={glassCardSX}>
          <VuiBox display="flex" justifyContent="space-between" mb={2}>
            <VuiTypography variant="h6">Notes History</VuiTypography>

            <VuiButton size="small" variant="outlined">
              <FaPlus size={10} style={{ marginRight: 6 }} />
              Add Note
            </VuiButton>
          </VuiBox>

          <NotesSection title="Arrival Notes" notes={arrivalNotes} />

          <NotesSection title="Staff Notes" notes={staffNotes} />

          <NotesSection title="PMS Notes" notes={pmsNotes} />
        </VuiBox>
      </DialogContent>
    </Dialog>
  );
};

export default GuestDetailsDialog;
